/**
 * Single renderer for every hardcoded product landing page. Content comes
 * from the typed per-product files in config/landing (edited via PR — no
 * page builder, no admin-entered HTML); the design stays consistent across
 * all products.
 */

import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Gleap from "gleap";
import { ArrowRight, Check, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CatalogDirectoryProduct } from "../../api/fetchProductCatalogDirectory";
import { LandingContent } from "../../config/landing";

interface ProductLandingProps {
  product: CatalogDirectoryProduct;
  categoryName: string;
  content: LandingContent;
}

export default function ProductLanding({
  product,
  categoryName,
  content,
}: ProductLandingProps) {
  const handleCta = () => Gleap.open();

  return (
    <div className="relative min-h-full">
      <Helmet>
        <title>{product.name}</title>
      </Helmet>

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#17064A] via-[#4E1240] to-[#B20030]">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <nav aria-label="Breadcrumb" className="mb-8">
            <ol className="flex items-center gap-1.5 text-sm text-white/70">
              <li>
                <Link
                  className="hover:text-white transition-colors"
                  to="/catalog"
                >
                  Product Catalog
                </Link>
              </li>
              <li aria-hidden="true">
                <ChevronRight className="h-4 w-4" />
              </li>
              <li className="text-[#FFC857]">{product.name}</li>
            </ol>
          </nav>

          <div className="max-w-3xl">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-white/20 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
                {categoryName}
              </span>
              {product.badge && (
                <span className="inline-flex items-center rounded-full bg-white/20 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
                  {product.badge}
                </span>
              )}
            </div>

            <h1 className="mt-4 text-4xl font-bold tracking-tight text-white md:text-5xl">
              {product.name}
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-white/90 md:text-xl">
              {product.tagline}
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-1.5">
              {[...product.audiences, ...product.trades].map((chip) => (
                <span
                  className="rounded-full bg-white/10 px-2.5 py-0.5 text-xs text-white/80"
                  key={chip}
                >
                  {chip}
                </span>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button
                className="gap-2 bg-white text-gray-900 hover:bg-white/90"
                onClick={handleCta}
                size="lg"
              >
                {content.ctaText}
                <ArrowRight className="h-5 w-5" />
              </Button>
              <a
                className="text-sm text-white/80 hover:text-white transition-colors"
                href="#how-it-works"
              >
                See how it works
              </a>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2">
              {content.quickFacts.map((fact) => (
                <span
                  className="flex items-center gap-1.5 text-sm text-white/80"
                  key={fact}
                >
                  <Check className="h-4 w-4 text-[#FFC857]" />
                  {fact}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Why */}
      <section className="max-w-7xl mx-auto px-4 py-14 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white md:text-3xl">
            {content.whyHeading}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-gray-500 dark:text-gray-400">
            {content.why}
          </p>
        </div>
      </section>

      {/* Advantages */}
      <section className="max-w-7xl mx-auto px-4 pb-14 sm:px-6 lg:px-8">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Built-in advantages
        </p>
        <h2 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
          Why teams choose {product.name}
        </h2>
        <p className="mt-2 text-base text-gray-500 dark:text-gray-400">
          Everything you need to make this product work for your business
        </p>

        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
          {content.advantages.map((advantage, index) => (
            <div
              className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6"
              key={advantage.title}
            >
              <span className="text-sm text-gray-400 dark:text-gray-500">
                {String(index + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-2 font-semibold text-gray-900 dark:text-white">
                {advantage.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                {advantage.description}
              </p>
              <p className="mt-4 flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {advantage.includedNote}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
          {content.featureChecklist.map((item) => (
            <span
              className="flex items-start gap-2 text-sm text-gray-900 dark:text-gray-100"
              key={item}
            >
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" />
              {item}
            </span>
          ))}
        </div>

        {content.highlights.length > 0 && (
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {content.highlights.map((highlight) => (
              <div
                className="rounded-lg border border-gray-100 dark:border-slate-700/50 bg-gray-50 dark:bg-slate-800/50 p-5"
                key={highlight.title}
              >
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {highlight.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                  {highlight.description}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* How it works */}
      <section
        className="max-w-7xl mx-auto px-4 pb-14 sm:px-6 lg:px-8 scroll-mt-6"
        id="how-it-works"
      >
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          How it works
        </h2>
        <p className="mt-2 text-base text-gray-500 dark:text-gray-400">
          A clear path from first conversation to consistent results
        </p>

        <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {content.steps.map((step, index) => (
            <div key={step.title}>
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-900 text-sm font-medium text-white dark:bg-white dark:text-gray-900">
                {index + 1}
              </span>
              <h3 className="mt-3 font-semibold text-gray-900 dark:text-white">
                {step.title}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      {content.testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 pb-14 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Success stories
          </h2>
          <p className="mt-2 text-base text-gray-500 dark:text-gray-400">
            Real results from teams running on {product.name}
          </p>

          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {content.testimonials.map((testimonial) => (
              <figure
                className="flex flex-col rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6"
                key={testimonial.name + testimonial.quote.slice(0, 24)}
              >
                <blockquote className="flex-1 text-sm leading-relaxed text-gray-900 dark:text-gray-100">
                  &ldquo;{testimonial.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#17064A] to-[#B20030] text-xs font-medium text-white">
                    {testimonial.initials}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-gray-900 dark:text-white">
                      {testimonial.name}
                    </span>
                    <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                      {testimonial.attribution}
                    </span>
                  </span>
                </figcaption>
                <div className="mt-4 flex gap-6 border-t border-gray-100 dark:border-slate-700/50 pt-4">
                  {testimonial.stats.map((stat) => (
                    <span className="min-w-0" key={stat.label}>
                      <span className="block text-base font-semibold text-gray-900 dark:text-white">
                        {stat.value}
                      </span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">
                        {stat.label}
                      </span>
                    </span>
                  ))}
                </div>
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {content.faqs.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 pb-14 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Frequently asked questions
          </h2>
          <p className="mt-2 text-base text-gray-500 dark:text-gray-400">
            Everything you need to know about {product.name}
          </p>

          <div className="mt-6 max-w-3xl divide-y divide-gray-200 dark:divide-slate-700 border-y border-gray-200 dark:border-slate-700">
            {content.faqs.map((faq) => (
              <details className="group py-4" key={faq.question}>
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-medium text-gray-900 dark:text-white [&::-webkit-details-marker]:hidden">
                  {faq.question}
                  <ChevronDown className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500 transition-transform group-open:rotate-180" />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* Closing CTA */}
      <div className="bg-gradient-to-tr from-[#17064A] via-[#6B1235] to-[#B20030]">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-white md:text-3xl">
            {content.closing.heading}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-white/80">
            {content.closing.body}
          </p>
          <div className="mt-8">
            <Button
              className="gap-2 bg-white text-gray-900 hover:bg-white/90"
              onClick={handleCta}
              size="lg"
            >
              {content.ctaText}
              <ArrowRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
